import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { FileText, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    setCurrentPage(0);
    loadLogs();
  }, []);

  useEffect(() => {
    if (allLogs.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setLogs(allLogs.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getLogs({}, 1000, 0); // Get all logs, paginate client-side
      if (response?.success) {
        const allLogsData = response.logs || [];
        setAllLogs(allLogsData);
        const start = currentPage * ITEMS_PER_PAGE;
        setLogs(allLogsData.slice(start, start + ITEMS_PER_PAGE));
      } else {
        setLogs([]);
        setAllLogs([]);
      }
    } catch (err) {
      console.error('Error loading logs:', err);
      setError(err.message || 'Failed to load logs');
      setLogs([]);
      setAllLogs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <FileText className="w-8 h-8 mr-3 text-gray-400" />
          Logs & Security
        </h1>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 mb-6 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          No logs found
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Admin</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Action</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Entity</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr key={idx} className="border-t border-gray-700">
                  <td className="px-4 py-3 text-sm">{log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}</td>
                  <td className="px-4 py-3 text-sm">{log.admin_username || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm">{log.action || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm">{log.entity_type || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && allLogs.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-center space-x-4 mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </button>
          <span className="text-gray-400 text-sm">
            Page {currentPage + 1} of {Math.ceil(allLogs.length / ITEMS_PER_PAGE)}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(Math.ceil(allLogs.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
            disabled={currentPage >= Math.ceil(allLogs.length / ITEMS_PER_PAGE) - 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage >= Math.ceil(allLogs.length / ITEMS_PER_PAGE) - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminLogs;

